// Lightweight fetch wrapper for the Rails API.
// Reads the CSRF token from the meta tag so POST/PUT/DELETE work
// against non-API controllers (the API namespace doesn't enforce CSRF
// since it inherits from ActionController::API, but sending it is
// harmless and keeps this wrapper usable for any controller).

import { rememberCreatorEmail } from "./creatorEmail"

function getCSRFToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

async function api(path, options = {}) {
  const { method = "GET", body, headers = {} } = options

  const config = {
    method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": getCSRFToken(),
      ...headers,
    },
  }

  if (body && method !== "GET") {
    config.body = JSON.stringify(body)
  }

  const response = await fetch(`/api/v1${path}`, config)

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || `API error: ${response.status}`)
  }

  if (response.status === 204) return null
  return response.json()
}

export default api

// ---------------------------------------------------------------------
// QR code creation
// ---------------------------------------------------------------------
//
// Direct fetch (not via the wrapper above) so the caller can react to
// specific status codes — 422 means user-fixable validation, 429 means
// too many requests, anything else means a generic problem the user
// can't act on. Throws ApiError with a typed `kind` so the UI can pick
// the right copy without re-parsing.

export class ApiError extends Error {
  constructor(kind, message, { appealUrl = null, details = null, field = null } = {}) {
    super(message)
    // 'validation' | 'rate_limit' | 'network' | 'unauthorized' | 'not_found'
    // | 'upgrade_required' | 'email_required' | 'email_invalid'
    // | 'verification_failed' | 'destination_unsafe' | 'destination_locked'
    // | 'view_only'
    this.kind = kind
    this.name = "ApiError"
    // Destination integrity: the signed re-check link for exactly what the
    // server refused (destination_unsafe) or has under review (destination_locked).
    this.appealUrl = appealUrl
    // Field errors from a refused save ({ footer_text: ["can't use ..."] }), so
    // the builder can put a message under the field it belongs to.
    this.details = details
    // Which form the details belong to, when the server says: "contact_page"
    // for a hosted contact page's fields (#332).
    this.field = field
  }
}

// The header and footer text errors in a refused save, keyed like the design
// state and capitalized to sit under the field. Null when the refusal was about
// something else (a color pair, say), so callers can keep their usual handling.
export function textFieldErrors(error) {
  const details = error?.details
  if (!details) return null
  const first = (key) => {
    const message = Array.isArray(details[key]) ? details[key][0] : null
    return message ? message.charAt(0).toUpperCase() + message.slice(1) : null
  }
  const errors = { headerText: first("header_text"), footerText: first("footer_text") }
  return errors.headerText || errors.footerText ? errors : null
}

// A staff view-as session is read-only: the server refuses every write with a
// 403 and code "view_only" (Middleware::ImpersonationReadOnly). The functions
// below that map status codes themselves call this first, so the refusal is
// not shown as an upgrade prompt or a network fault. The `api()` wrapper needs
// no help: it already shows the server's own message.
async function rejectIfViewOnly(response) {
  if (response.status !== 403) return
  const body = await response.clone().json().catch(() => ({}))
  if (body.code === "view_only") throw new ApiError("view_only", body.error)
}

// Destination-integrity refusals carry a machine-readable code and a re-check
// link. Nothing was saved, so the caller shows the message on the URL field.
function destinationIntegrityError(body) {
  if (body.code !== "destination_unsafe" && body.code !== "destination_locked") return null
  return new ApiError(body.code, body.error || "We couldn't save that destination.", { appealUrl: body.appeal_url })
}

// `params` can be either a plain URL string (back-compat) or an object with
// design fields. When `logo` is a File, the request is sent as multipart so
// the server can stream the bytes through ActiveStorage in one round-trip.
//
// `botProtection` is optional — `{ honeypot, formStartedAt, turnstileToken }`.
// The server fails open on missing Turnstile when the secret is unset, but
// always evaluates honeypot + timing. See the Bot Protection playbook.
export async function createQrCode(params, botProtection = null) {
  const normalized = typeof params === "string"
    ? { destinationUrl: params }
    : params || {}

  let response
  try {
    response = await fetch("/api/v1/qr_codes", {
      method: "POST",
      credentials: "same-origin",
      ...buildQrRequestInit(normalized, botProtection)
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't reach our server. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 201) {
    // The login page fills this in later (#319). See lib/creatorEmail.js.
    if (normalized.email) rememberCreatorEmail(normalized.email)
    return response.json()
  }

  if (response.status === 422) {
    const body = await response.json().catch(() => ({}))
    // The email gate returns a machine-readable `code` alongside the message so
    // the builder can tell "we need an email" from "that URL is bad" — and can
    // reveal the email field inline if this page was rendered before the flag
    // flipped. Anything without a `code` is ordinary validation.
    if (body.code === "email_required" || body.code === "email_invalid") {
      throw new ApiError(body.code, body.error || "Enter an email so we can send you the code.")
    }
    // The bot layer refused us. One code for all three server-side signals, so
    // it says nothing about which one fired -- it only lets the builder put the
    // error on the form instead of on the URL field (the URL is fine) and reset
    // the Turnstile widget so a retry can actually succeed. Issue #166.
    if (body.code === "verification_failed") {
      throw new ApiError(
        "verification_failed",
        body.error || "We couldn't verify your browser. Try again in a moment."
      )
    }
    const integrityError = destinationIntegrityError(body)
    if (integrityError) throw integrityError
    throw new ApiError("validation", body.error || "We couldn't accept that URL.", { details: body.details, field: body.field })
  }

  if (response.status === 429) {
    throw new ApiError(
      "rate_limit",
      "You've made a lot of codes lately. Take a breath and try again in a minute."
    )
  }

  // Contact pages are off (#332): the page is stale, the feature isn't there.
  if (response.status === 404 && normalized.contactPage) {
    throw new ApiError("not_found", "Contact pages aren't available right now. Pick Website or Email instead.")
  }

  throw new ApiError("network", "Couldn't reach our server. Try again in a moment.")
}

// `email` is sent at the TOP level in both encodings (not under `qr_code[...]`)
// — the server accepts either shape, and keeping it flat means the multipart
// branch can't silently drop it the way a nested-only read would. Same for
// `referenceToken`, the code whose look a new code starts from (#343).
//
// `contactPage` (#332) is a hosted contact page's fields, top-level
// `contact_page` in both encodings. With it the server ignores any destination
// and points the code at the page.
function buildQrRequestInit({ destinationUrl, label, foregroundColor, backgroundColor, gradientColor, gradientType, frameStyle, headerText, footerText, textWeight, moduleShape, eyeShape, logo, logoSize, removeLogo, email, referenceToken, contactPage }, botProtection) {
  const bp = botProtection || {}
  const hasFile = logo instanceof File
  if (hasFile) {
    const fd = new FormData()
    if (contactPage) Object.entries(contactPage).forEach(([key, value]) => fd.append(`contact_page[${key}]`, value || ""))
    if (destinationUrl !== undefined && !contactPage) fd.append("qr_code[destination_url]", destinationUrl)
    if (label !== undefined)           fd.append("qr_code[label]", label || "")
    if (foregroundColor !== undefined) fd.append("qr_code[foreground_color]", foregroundColor || "")
    if (backgroundColor !== undefined) fd.append("qr_code[background_color]", backgroundColor || "")
    if (gradientColor !== undefined)   fd.append("qr_code[gradient_color]", gradientColor || "")
    if (gradientType !== undefined)    fd.append("qr_code[gradient_type]", gradientType || "")
    if (frameStyle !== undefined)      fd.append("qr_code[frame_style]", frameStyle || "")
    if (headerText !== undefined)      fd.append("qr_code[header_text]", headerText || "")
    if (footerText !== undefined)      fd.append("qr_code[footer_text]", footerText || "")
    if (textWeight !== undefined)      fd.append("qr_code[text_weight]", textWeight || "")
    if (moduleShape !== undefined)     fd.append("qr_code[module_shape]", moduleShape || "")
    if (eyeShape !== undefined)        fd.append("qr_code[eye_shape]", eyeShape || "")
    if (logoSize !== undefined)        fd.append("qr_code[logo_size]", logoSize || "")
    if (removeLogo)                    fd.append("qr_code[remove_logo]", "true")
    fd.append("qr_code[logo]", logo)
    if (email)                         fd.append("email", email)
    if (referenceToken)                fd.append("reference_token", referenceToken)
    if (bp.honeypot !== undefined)       fd.append("company", bp.honeypot || "")
    if (bp.formStartedAt !== undefined)  fd.append("form_started_at", String(bp.formStartedAt))
    if (bp.turnstileToken !== undefined) fd.append("cf-turnstile-response", bp.turnstileToken || "")
    return {
      headers: { Accept: "application/json" },
      body: fd
    }
  }
  const body = {}
  if (contactPage)                  body.contact_page = contactPage
  if (destinationUrl !== undefined && !contactPage) body.destination_url = destinationUrl
  if (label !== undefined)           body.label = label || ""
  if (foregroundColor !== undefined) body.foreground_color = foregroundColor || ""
  if (backgroundColor !== undefined) body.background_color = backgroundColor || ""
  if (gradientColor !== undefined)   body.gradient_color = gradientColor || ""
  if (gradientType !== undefined)    body.gradient_type = gradientType || ""
  if (frameStyle !== undefined)      body.frame_style = frameStyle || ""
  if (headerText !== undefined)      body.header_text = headerText || ""
  if (footerText !== undefined)      body.footer_text = footerText || ""
  if (textWeight !== undefined)      body.text_weight = textWeight || ""
  if (moduleShape !== undefined)     body.module_shape = moduleShape || ""
  if (eyeShape !== undefined)        body.eye_shape = eyeShape || ""
  if (logoSize !== undefined)        body.logo_size = logoSize || ""
  if (removeLogo)                    body.remove_logo = true
  if (email)                         body.email = email
  if (referenceToken)                body.reference_token = referenceToken
  if (bp.honeypot !== undefined)       body.company = bp.honeypot || ""
  if (bp.formStartedAt !== undefined)  body.form_started_at = bp.formStartedAt
  if (bp.turnstileToken !== undefined) body["cf-turnstile-response"] = bp.turnstileToken || ""
  return {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body)
  }
}

// Live preview for the builder + dashboard drawer. Same render path as the
// real code, no persistence. Multipart when `logo` is a File, JSON otherwise.
// `token` is optional — when set, the server encodes that code's real short
// URL (tgo.sh/:token) as the QR payload so the preview's module pattern matches
// the printed code. The edit modal passes it; the create builder omits it.
// `referenceToken` is the code a new one starts from (#343): the server fills
// in that code's saved logo but keeps the generic payload.
export async function previewQrCode({ token, referenceToken, foregroundColor, backgroundColor, gradientColor, gradientType, frameStyle, headerText, footerText, textWeight, moduleShape, eyeShape, logo, logoSize, removeLogo, wordmark } = {}) {
  const hasFile = logo instanceof File
  const init = hasFile
    ? {
        headers: { Accept: "application/json" },
        body: (() => {
          const fd = new FormData()
          if (token !== undefined)           fd.append("qr_code[token]", token || "")
          if (referenceToken)                fd.append("qr_code[reference_token]", referenceToken)
          if (foregroundColor !== undefined) fd.append("qr_code[foreground_color]", foregroundColor || "")
          if (backgroundColor !== undefined) fd.append("qr_code[background_color]", backgroundColor || "")
          if (gradientColor !== undefined)   fd.append("qr_code[gradient_color]", gradientColor || "")
          if (gradientType !== undefined)    fd.append("qr_code[gradient_type]", gradientType || "")
          if (frameStyle !== undefined)      fd.append("qr_code[frame_style]", frameStyle || "")
          if (headerText !== undefined)      fd.append("qr_code[header_text]", headerText || "")
          if (footerText !== undefined)      fd.append("qr_code[footer_text]", footerText || "")
          if (textWeight !== undefined)      fd.append("qr_code[text_weight]", textWeight || "")
          if (moduleShape !== undefined)     fd.append("qr_code[module_shape]", moduleShape || "")
          if (eyeShape !== undefined)        fd.append("qr_code[eye_shape]", eyeShape || "")
          if (logoSize !== undefined)        fd.append("qr_code[logo_size]", logoSize || "")
          if (wordmark !== undefined)        fd.append("qr_code[wordmark]", String(wordmark))
          fd.append("qr_code[logo]", logo)
          return fd
        })()
      }
    : {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          qr_code: {
            ...(token !== undefined           ? { token: token || "" } : {}),
            ...(referenceToken                ? { reference_token: referenceToken } : {}),
            ...(foregroundColor !== undefined ? { foreground_color: foregroundColor || "" } : {}),
            ...(backgroundColor !== undefined ? { background_color: backgroundColor || "" } : {}),
            ...(gradientColor !== undefined   ? { gradient_color: gradientColor || "" } : {}),
            ...(gradientType !== undefined    ? { gradient_type: gradientType || "" } : {}),
            ...(frameStyle !== undefined      ? { frame_style: frameStyle || "" } : {}),
            // Sent even when blank: a blank clears the saved text, an absent key
            // keeps it (the server's preview rule).
            ...(headerText !== undefined      ? { header_text: headerText || "" } : {}),
            ...(footerText !== undefined      ? { footer_text: footerText || "" } : {}),
            ...(textWeight !== undefined      ? { text_weight: textWeight || "" } : {}),
            ...(moduleShape !== undefined     ? { module_shape: moduleShape || "" } : {}),
            ...(eyeShape !== undefined        ? { eye_shape: eyeShape || "" } : {}),
            ...(logoSize !== undefined        ? { logo_size: logoSize || "" } : {}),
            // No new file but the user cleared the saved logo — tell the server
            // to render logo-less instead of falling back to the persisted one.
            ...(removeLogo                    ? { remove_logo: true } : {}),
            ...(wordmark !== undefined        ? { wordmark: !!wordmark } : {})
          }
        })
      }

  let response
  try {
    response = await fetch("/api/v1/qr_codes/preview", {
      method: "POST",
      credentials: "same-origin",
      ...init
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't render preview.")
  }

  if (response.status === 200) return response.json()
  if (response.status === 422) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError("validation", body.error || "Couldn't render preview.")
  }
  throw new ApiError("network", "Couldn't render preview.")
}

export async function saveCodeToInbox(token, email, botProtection = null) {
  const bp = botProtection || {}
  const body = { email }
  if (bp.honeypot !== undefined)       body.company = bp.honeypot || ""
  if (bp.formStartedAt !== undefined)  body.form_started_at = bp.formStartedAt
  if (bp.turnstileToken !== undefined) body["cf-turnstile-response"] = bp.turnstileToken || ""


  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/email`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't send right now. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) {
    rememberCreatorEmail(email)
    return response.json()
  }

  if (response.status === 422) {
    const body = await response.json().catch(() => ({}))
    // #email runs the same bot layer as #create and returns the same code.
    // Without this branch the refusal was reported as an email-format problem,
    // reddening a perfectly good address and telling the visitor to fix
    // something that isn't broken. Issue #166.
    if (body.code === "verification_failed") {
      throw new ApiError(
        "verification_failed",
        body.error || "We couldn't verify your browser. Try again in a moment."
      )
    }
    throw new ApiError("validation", body.error || "That doesn't look like a valid email.")
  }

  if (response.status === 429) {
    throw new ApiError(
      "rate_limit",
      "You've saved a lot of codes. Try again in an hour."
    )
  }

  throw new ApiError("network", "Couldn't send right now. Try again in a moment.")
}

// Re-send the "code saved" email to the address already captured on this code.
// The recovery path when the first email never arrived — no email argument, the
// server resends to the stored address (so it can't be pointed elsewhere).
export async function resendCodeToInbox(token) {
  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/resend_email`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: "{}",
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't resend right now. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) return response.json()

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError("rate_limit", body.error || "We just re-sent that code. Try again in an hour.")
  }

  if (response.status === 422) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError("validation", body.error || "Couldn't resend that code.")
  }

  throw new ApiError("network", "Couldn't resend right now. Try again in a moment.")
}

// The newest code this browser made and can still work on, or null. Feeds the
// homepage's "Your recent code" card (issue #264).
//
// Takes no argument on purpose: the server reads the httponly creation-session
// cookie, which this script cannot see and must not be able to name a code for.
//
// Never throws. The card is a convenience above a form that has to work without
// it, so a failed, throttled or slow request reads the same as "no recent
// code". `cache: "no-store"` matches the response header: the body depends on
// the cookie alone and must not be replayed from any cache.
export async function fetchRecentCode() {
  try {
    const response = await fetch("/api/v1/qr_codes/recent", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
    if (response.status !== 200) return null
    const body = await response.json()
    return body?.code || null
  } catch (e) {
    return null
  }
}

export async function listQrCodes() {
  let response
  try {
    response = await fetch("/api/v1/qr_codes", {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load your codes. Try again.")
  }

  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load your codes. Try again.")
}

// Loads the per-code analytics payload. Sections the plan doesn't entitle are
// **omitted** by the server (`AnalyticsGate`), so `charts`, `unique_devices`,
// `top_country` and `last_scan_at` are optional and `paywall.locked_sections`
// names what was withheld. Callers render what arrived rather than re-deriving
// the gate from `paywall.locked`. `paywall.applies` is false when the viewer's
// plan has no scan window (Pro), so no free-window copy belongs on the page. See
// `docs/specs/2026-09-12-analytics-entitlement-enforcement.md`.
export async function fetchAnalytics(token) {
  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/analytics`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load analytics. Try again.")
  }

  if (response.status === 200) return response.json()
  if (response.status === 404) throw new ApiError("not_found", "This code is no longer yours.")
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load analytics. Try again.")
}

// Loads the account-wide analytics rollup (portfolio across all of the user's
// codes). `range` windows the trend block; the leaderboard + audience are
// all-time. Sections the plan doesn't entitle are **omitted** by the server
// (`AnalyticsGate`), so callers must treat `charts` and the detail `totals`
// keys as optional and read `paywall.locked_sections` for what was withheld.
// Mirrors fetchAnalytics's three-state contract (loading / ready / error).
// `group` (a group id, "ungrouped", or empty) re-scopes the whole report.
export async function fetchAccountAnalytics(range = "30d", group = "") {
  const params = new URLSearchParams({ range })
  if (group) params.set("group", String(group))
  let response
  try {
    response = await fetch(`/api/v1/analytics?${params.toString()}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load analytics. Try again.")
  }

  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load analytics. Try again.")
}

// URL for the CSV download of the account analytics page — a plain link, so
// the browser handles the file. `rows` is "codes" | "days" | "scans"; range and
// group are the page's own, so the file matches what's on screen. The server
// gates each row exactly as the page is gated (Analytics::Export).
export function accountAnalyticsExportUrl(rows, range = "30d", group = "") {
  const params = new URLSearchParams({ rows, range })
  if (group) params.set("group", String(group))
  return `/api/v1/analytics/export?${params.toString()}`
}

// Accepts either a string (destination URL only, back-compat) or an object
// with design fields. Same multipart-when-logo-File contract as createQrCode.
export async function updateQrCode(token, params) {
  const normalized = typeof params === "string"
    ? { destinationUrl: params }
    : params || {}

  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}`, {
      method: "PATCH",
      credentials: "same-origin",
      ...buildQrRequestInit(normalized)
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't save. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) return response.json()
  if (response.status === 422 || response.status === 423) {
    const body = await response.json().catch(() => ({}))
    const integrityError = destinationIntegrityError(body)
    if (integrityError) throw integrityError
    throw new ApiError("validation", body.error || "We couldn't accept that URL.", { details: body.details })
  }
  if (response.status === 403) {
    throw new ApiError("upgrade_required", "Editing a code's destination is a paid feature.")
  }
  if (response.status === 404) throw new ApiError("not_found", "This code is no longer available.")
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't save. Try again in a moment.")
}

// ---------------------------------------------------------------------
// A code's hosted contact page (#332), for its signed-in owner
// ---------------------------------------------------------------------
//
// GET  → { contact_page: { first_name, …, url, in_use }, code: row } or null (no page)
// PUT  → the same, after ContactPages::Change (a page edit is a destination
//        edit: 403 past the free scan limit, 423 under review, 422 refused)
// DELETE → { code: row }. Removal works at any scan count, on any plan.

async function contactPageRequest(token, method, body = undefined) {
  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/contact_page`, {
      method,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't reach our server. Try again in a moment.")
  }
  await rejectIfViewOnly(response)
  return response
}

export async function getContactPage(token) {
  const response = await contactPageRequest(token, "GET")
  if (response.status === 200) return response.json()
  if (response.status === 404) return null
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load the contact page. Try again.")
}

export async function saveContactPage(token, fields) {
  const response = await contactPageRequest(token, "PUT", { contact_page: fields })
  if (response.status === 200) return response.json()
  const body = await response.json().catch(() => ({}))
  if (response.status === 422 || response.status === 423) {
    const integrityError = destinationIntegrityError(body)
    if (integrityError) throw integrityError
    throw new ApiError("validation", body.error || "Check the page's details.", { details: body.details, field: "contact_page" })
  }
  if (response.status === 403) throw new ApiError("upgrade_required", body.error || "Editing this code needs Pro.")
  if (response.status === 404) throw new ApiError("not_found", "Contact pages aren't available right now.")
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't save. Try again in a moment.")
}

export async function removeContactPage(token) {
  const response = await contactPageRequest(token, "DELETE")
  if (response.status === 200) return response.json()
  if (response.status === 404) throw new ApiError("not_found", "This page was already removed.")
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't remove the page. Try again in a moment.")
}

// Design-only restyle of a freshly-created anonymous code from the landing
// builder. Same multipart-when-logo-File contract as createQrCode, but the
// server permits only design fields — destination edits go through
// updateQrCode (authenticated + scan-gated). Returns the serialized row,
// whose `image_url` carries the new design digest so the displayed/downloaded
// image updates on its own.
export async function restyleQrCode(token, params) {
  const normalized = typeof params === "string" ? {} : (params || {})

  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/restyle`, {
      method: "PATCH",
      credentials: "same-origin",
      ...buildQrRequestInit({
        foregroundColor: normalized.foregroundColor,
        backgroundColor: normalized.backgroundColor,
        gradientColor:   normalized.gradientColor,
        gradientType:    normalized.gradientType,
        frameStyle:      normalized.frameStyle,
        headerText:      normalized.headerText,
        footerText:      normalized.footerText,
        textWeight:      normalized.textWeight,
        moduleShape:     normalized.moduleShape,
        eyeShape:        normalized.eyeShape,
        logo:            normalized.logo,
        logoSize:        normalized.logoSize,
        removeLogo:      normalized.removeLogo
      })
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't save your design. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) return response.json()
  if (response.status === 422) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError("validation", body.error || "Couldn't apply that design.", { details: body.details })
  }
  if (response.status === 404) throw new ApiError("not_found", "This code is no longer available.")
  throw new ApiError("network", "Couldn't save your design. Try again in a moment.")
}

export async function disclaimQrCode(token) {
  let response
  try {
    response = await fetch(`/api/v1/qr_codes/${encodeURIComponent(token)}/claim`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't remove. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 204) return true
  if (response.status === 404) throw new ApiError("not_found", "This code is no longer yours.")
  if (response.status === 422) throw new ApiError("validation", "Already removed.")
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't remove. Try again in a moment.")
}

export async function acknowledgeClaims() {
  let response
  try {
    response = await fetch("/api/v1/qr_codes/acknowledge_claims", {
      method: "PATCH",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't dismiss. Try again in a moment.")
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't dismiss. Try again in a moment.")
}

// ---------------------------------------------------------------------
// Codes made with this account's address on another device (#329)
// ---------------------------------------------------------------------
//
// Listed only after a sign-in that proved the address, and added only on the
// owner's press. The POSTs send the tokens the card showed; the server
// re-checks each one and ignores any that no longer qualify.

// { codes: [{ token, display_label, destination_url, short_url, image_url, created_at }] }
export function fetchClaimOffer() {
  return api("/claim_offer")
}

// { added: n }
export function acceptClaimOffer(tokens) {
  return api("/claim_offer/accept", { method: "POST", body: { tokens } })
}

// { dismissed: n }
export function dismissClaimOffer(tokens) {
  return api("/claim_offer/dismiss", { method: "POST", body: { tokens } })
}

// ---------------------------------------------------------------------
// Code groups (folders) — free on every plan
// ---------------------------------------------------------------------
//
// The `api()` wrapper throws Error(body.error) on non-2xx, so a duplicate name
// or the per-request cap surfaces the server's own message.

// { groups: [{ id, name, code_count }], ungrouped_count, total }
export function listCodeGroups() {
  return api("/code_groups")
}

export function createCodeGroup(name) {
  return api("/code_groups", { method: "POST", body: { name } })
}

export function renameCodeGroup(id, name) {
  return api(`/code_groups/${encodeURIComponent(id)}`, { method: "PATCH", body: { name } })
}

// Ungroups the group's codes; never deletes or disables one.
export function deleteCodeGroup(id) {
  return api(`/code_groups/${encodeURIComponent(id)}`, { method: "DELETE" })
}

// Move a selection into `groupId` (null = no group). Pass either `tokens` or
// `match: { q, group }` — the list's own search + filter, for "select all N".
export function assignCodesToGroup({ groupId, tokens, match }) {
  const body = { group_id: groupId ?? null }
  if (tokens) body.tokens = tokens
  if (match) body.match = match
  return api("/code_groups/assign", { method: "POST", body })
}

// ---------------------------------------------------------------------
// Bulk repoint (docs/plans/2026-09-20-bulk-repoint.plan.md)
// ---------------------------------------------------------------------
//
// Preview is a server-side dry run: it returns the exact roster the write
// would produce — { matched, updated, unchanged, skipped: [{ token, reason }],
// budget_remaining } — because `code_editable?` is a server predicate and the
// SPA must not carry a second copy of the 50-scan rule.
//
// A request is either one URL over a selection ({ destinationUrl, tokens } or
// { destinationUrl, match }) or a CSV file ({ file }) that names its own codes.

export function previewRepoint(request) {
  return repointRequest("/code_edits/preview", request)
}

export function commitRepoint(request) {
  return repointRequest("/code_edits", request)
}

export function undoRepoint(batchId) {
  return api(`/code_edits/${batchId}/undo`, { method: "POST" })
}

async function repointRequest(path, { destinationUrl, tokens, match, file }) {
  if (!file) {
    const body = { destination_url: destinationUrl }
    if (tokens) body.tokens = tokens
    if (match) body.match = match
    return api(path, { method: "POST", body })
  }

  // Multipart, so no JSON Content-Type — the browser sets the boundary.
  const form = new FormData()
  form.append("file", file)
  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCSRFToken() },
    body: form,
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || `API error: ${response.status}`)
  }
  return response.json()
}

// ---------------------------------------------------------------------
// Spreadsheet creation (docs/plans/2026-09-23-spreadsheet-creation.plan.md)
// ---------------------------------------------------------------------
//
// Preview validates every row and writes nothing:
//   { creatable, invalid: [{ line, reason }], new_groups, shapes_dropped, budget_remaining }
// Create queues the batch (202) and returns its status, which the dialog polls:
//   { id, status, requested, created_count, skipped_count, created: [{ line, token }],
//     skipped: [{ line, reason }], bundle_ready }
//
// A request is pasted `text` or a CSV `file`, plus an optional `groupId` for
// rows without a folder of their own and a `referenceToken` to copy a code's
// style from.

export function previewCodeCreate(request) {
  return codeCreateRequest("/code_creates/preview", request)
}

export function createCodes(request) {
  return codeCreateRequest("/code_creates", request)
}

export function getCodeCreate(id) {
  return api(`/code_creates/${encodeURIComponent(id)}`)
}

// A plain link: the browser follows the redirect to the file.
export function codeCreateDownloadUrl(id, file = "zip") {
  return `/api/v1/code_creates/${encodeURIComponent(id)}/download?file=${file}`
}

async function codeCreateRequest(path, { text, file, groupId, referenceToken }) {
  if (!file) {
    const body = { text }
    if (groupId) body.group_id = groupId
    if (referenceToken) body.reference_token = referenceToken
    return api(path, { method: "POST", body })
  }

  // Multipart, so no JSON Content-Type — the browser sets the boundary.
  const form = new FormData()
  form.append("file", file)
  if (groupId) form.append("group_id", groupId)
  if (referenceToken) form.append("reference_token", referenceToken)
  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { Accept: "application/json", "X-CSRF-Token": getCSRFToken() },
    body: form,
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || `API error: ${response.status}`)
  }
  return response.json()
}

// ---------------------------------------------------------------------
// Developer API — keys & webhooks management (Pro only, cookie-authed)
// ---------------------------------------------------------------------
//
// These manage the credentials used against the key-authed public API at
// /api/public/v1. The `api()` wrapper throws Error(body.error) on non-2xx, so
// callers can surface the server message directly (e.g. an invalid webhook URL).

export function listApiKeys() {
  return api("/api_keys")
}

// Returns the created key INCLUDING its one-time plaintext `token`.
export function createApiKey({ name, scopes }) {
  return api("/api_keys", { method: "POST", body: { name, scopes } })
}

export function revokeApiKey(id) {
  return api(`/api_keys/${id}`, { method: "DELETE" })
}

export function listWebhooks() {
  return api("/webhooks")
}

// Returns the created webhook INCLUDING its one-time signing `secret`.
export function createWebhook({ url }) {
  return api("/webhooks", { method: "POST", body: { url } })
}

export function updateWebhook(id, attrs) {
  return api(`/webhooks/${id}`, { method: "PATCH", body: attrs })
}

export function deleteWebhook(id) {
  return api(`/webhooks/${id}`, { method: "DELETE" })
}

// ---------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------
//
// Both endpoints return a Stripe-hosted URL; the caller is expected to
// send the browser there (window.location.assign).

async function billingRedirectUrl(path, friendlyError, { body } = {}) {
  let response
  try {
    response = await fetch(`/api/v1${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
  } catch (e) {
    throw new ApiError("network", friendlyError)
  }
  await rejectIfViewOnly(response)

  if (response.status === 200) {
    const payload = await response.json().catch(() => ({}))
    if (!payload.url) throw new ApiError("network", friendlyError)
    return payload.url
  }
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  if (response.status === 422) {
    const payload = await response.json().catch(() => ({}))
    throw new ApiError("no_billing_account", payload.error || "No billing account yet.")
  }
  throw new ApiError("network", friendlyError)
}

// Starts a Pro subscription checkout and navigates to Stripe Checkout.
// `period` is "monthly" (default) or "annual"; both bill back to the same
// Pro entitlements.
export async function startCheckout(period = "monthly") {
  const url = await billingRedirectUrl(
    "/billing/checkout_session",
    "Couldn't start checkout. Try again in a moment.",
    { body: { period } }
  )
  window.location.assign(url)
}

// Opens the Stripe customer portal (manage / cancel subscription).
export async function openBillingPortal() {
  const url = await billingRedirectUrl(
    "/billing/portal_session",
    "Couldn't open the billing portal. Try again in a moment."
  )
  window.location.assign(url)
}

// Permanently deletes (anonymizes) the signed-in account and signs them out.
// 204 on success. The caller redirects away afterward — the session is gone.
export function deleteAccount() {
  return api("/me", { method: "DELETE" })
}

// Marketing consent toggle on the profile page. One of the three unsubscribe
// paths the privacy page promises — the other two are the link in every
// broadcast and the RFC 8058 one-click header. Returns the resulting state
// rather than echoing the request, so a suppressed address (bounced, so
// blocked regardless of consent) reports honestly.
export function updateEmailPreferences(marketingConsent) {
  return api("/me/email_preferences", {
    method: "PATCH",
    body: { marketing_consent: marketingConsent },
  })
}

// The Notifications section of Profile (#304). Returns
// { categories: { marketing, digest, scan_alerts, quiet_alerts }, digest_frequency,
//   timezone, scan_alert_thresholds, never_scanned_days, quiet_days,
//   day_choices, digest_notice_dismissed, features: { ... } }.
// `features` says which parts to show: a part whose email can't reach this
// account stays hidden.
export function fetchNotificationSettings() {
  return api("/me/notification_settings")
}

// Send only the fields that changed. Returns the whole settings payload.
export function updateNotificationSettings(attrs) {
  return api("/me/notification_settings", { method: "PATCH", body: attrs })
}

// Hides the dashboard's "download a clean copy" notice for a new Pro
// subscriber (#343). Saved on the subscription, so it stays gone everywhere.
export function dismissWordmarkNotice() {
  return api("/me/wordmark_notice", { method: "DELETE" })
}

// On/off for the address-keyed email categories: `scan_alerts` and
// `quiet_alerts` (booleans). Returns { marketing_consent, categories }, the
// server's state, so a suppressed (bounced) address reports honestly.
export function updateEmailCategories(attrs) {
  return api("/me/email_preferences", { method: "PATCH", body: attrs })
}

// Scan-alert overrides on one code or one group (#304). Each returns
// { rule: { scan_alert_thresholds, quiet_alerts_muted } | null,
//   effective: { scan_alert_thresholds, thresholds_from, quiet_alerts_muted, muted_from },
//   group: { id, name } | null, features: { ... } }.
// `*_from` is "code", "group" or "account": where the value that applies comes
// from. In an update, null means "use the next level"; a key left out is left
// alone.
function alertRulePath(target) {
  return target.token
    ? `/qr_codes/${encodeURIComponent(target.token)}/alert_rule`
    : `/code_groups/${encodeURIComponent(target.groupId)}/alert_rule`
}

export function fetchAlertRule(target) {
  return api(alertRulePath(target))
}

export function updateAlertRule(target, attrs) {
  return api(alertRulePath(target), { method: "PUT", body: attrs })
}

export function clearAlertRule(target) {
  return api(alertRulePath(target), { method: "DELETE" })
}

// ---------------------------------------------------------------------
// Dashboard data — user-scoped summary endpoints under /api/v1/me/
// ---------------------------------------------------------------------

// Returns { total_lifetime, code_count, daily: [{date, count}] }.
// Bundles the 7-day chart data with the lifetime tile so the dashboard
// can render both with a single round-trip.
export async function fetchMeScans() {
  let response
  try {
    response = await fetch("/api/v1/me/scans", {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load scan totals. Try again.")
  }
  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load scan totals. Try again.")
}

// Returns the last N scans as [{ code_token, code_label, country_code, scanned_at }].
// Polled by RecentScansFeed while the tab is visible.
export async function fetchRecentScans({ limit = 10 } = {}) {
  let response
  try {
    response = await fetch(`/api/v1/me/scans/recent?limit=${encodeURIComponent(limit)}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load recent scans. Try again.")
  }
  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load recent scans. Try again.")
}

// Paginated codes list for the dashboard. Returns
// { codes, page, per, total, total_pages }. The row shape matches
// the existing listQrCodes() rows (shared serializer concern on the
// backend).
export async function fetchMeCodes({ page = 1, per = 10, q = "", sort = "", group = "" } = {}) {
  const params = new URLSearchParams({ page: String(page), per: String(per) })
  if (q) params.set("q", q)
  if (sort) params.set("sort", sort)
  if (group) params.set("group", String(group))
  const qs = params.toString()
  let response
  try {
    response = await fetch(`/api/v1/me/codes?${qs}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
  } catch (e) {
    throw new ApiError("network", "Couldn't load your codes. Try again.")
  }
  if (response.status === 200) return response.json()
  if (response.status === 401) throw new ApiError("unauthorized", "Please sign in.")
  throw new ApiError("network", "Couldn't load your codes. Try again.")
}
